import { ChartStatusEnum, confirmHospitalizationEventOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { formatToDateTwoYearAndTimeOrNull } from '~/util/date';
import { formatStringToTitleCase } from '~/util/functions';
import { PatientNameTableCell } from '~/features/intake/patients/PatientNameTableCell';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useMutation } from '~/util/http/useMutation';
import { useQueryClient } from '@tanstack/react-query';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { useNavigate } from 'react-router-dom';
import { getPatientLegacyHospitalizationRoute } from '../util';
import { TableActions } from '~/shared/data-tables/TableActions';

export function RenderFacilityNameCell({
	facilityName,
	facilityCity,
	facilityState,
	facilityZipCode
}: {
	facilityName?: string;
	facilityCity?: string;
	facilityState?: string;
	facilityZipCode?: string;
}) {
	return (
		<div className="flex w-full flex-col overflow-hidden">
			<div className="flex w-full min-w-0 items-center gap-2">
				<span className="truncate text-sm font-semibold text-gray-900">{facilityName ?? 'N/A'}</span>
			</div>

			<p className="mt-1 max-w-xs truncate text-xs text-gray-500">
				{facilityCity ?? ''}, {facilityState} {facilityZipCode}
			</p>
		</div>
	);
}

const LegacyHospitalizationVisitsDataTable = createQueryDataTable('careport-visits');
type RowType = RowOfQueryDataTable<typeof LegacyHospitalizationVisitsDataTable>;
type TableProps = PropsOfQueryDataTable<typeof LegacyHospitalizationVisitsDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const markAsConfirmed = useMutation(confirmHospitalizationEventOptions);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const onMarkAsConfirmed = async () => {
		await markAsConfirmed.mutateAsync(
			{ eventId: row.id },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: getSearchQueryKey('careport-visits') });
				}
			}
		);
	};

	const onViewDetails = () => {
		navigate(getPatientLegacyHospitalizationRoute(row.sponsorMrn, row.stayUuid!));
	};

	return (
		<TableActions>
			{row.stayUuid && <TableActions.ViewButton aria-label="View" onClick={onViewDetails} />}
			<TableActions.Button Icon={CheckCircle} aria-label="Confirm" useTooltip isDisabled={!!row.confirmed} onClick={onMarkAsConfirmed} />
		</TableActions>
	);
}

export function getChartStatusId(chartStatus?: string): number | undefined {
	if (!chartStatus) {
		return undefined;
	} else if (chartStatus === 'Admitted') {
		return ChartStatusEnum.Admitted;
	} else if (chartStatus === 'Pre-Admit') {
		return ChartStatusEnum.PreAdmitted;
	} else if (chartStatus === 'Discharged' || chartStatus === 'Transferred') {
		return ChartStatusEnum.Discharged;
	} else if (chartStatus === 'Hospitalized') {
		return ChartStatusEnum.Hospitalized;
	} else if (chartStatus === 'On Hold') {
		return ChartStatusEnum.OnHold;
	} else if (chartStatus === 'NTFC') {
		return ChartStatusEnum.NTFC;
	} else if (chartStatus === 'Observation') {
		return ChartStatusEnum.Observation;
	} else if (chartStatus === 'Do Not Admit') {
		return ChartStatusEnum.NTFC;
	}
	return undefined;
}

const columnDefinitions = LegacyHospitalizationVisitsDataTable.columns([
	{
		header: 'Patient',
		key: 'patientName',
		sortable: true,
		width: 'w-[250px]',
		renderCell: row => (
			<PatientNameTableCell
				firstName={row.patientFirstName ?? 'Unknown'}
				lastName={row.patientLastName ?? 'Unknown'}
				patientIdentifier={row.sponsorMrn}
				chartStatusId={getChartStatusId(row.chartStatus)}
			/>
		)
	},
	{
		header: 'Team',
		key: 'patientTeam',
		sortable: true,
		renderCell: row => (row.patientTeam ? formatStringToTitleCase(row.patientTeam) : 'N/A')
	},
	{
		header: 'Facility',
		key: 'facilityName',
		cellClassName: 'overflow-hidden',
		width: 'w-[250px]',
		sortable: true,
		renderCell: row => (
			<RenderFacilityNameCell
				facilityName={row.facilityName}
				facilityCity={row.facilityCity}
				facilityState={row.faciltyState}
				facilityZipCode={row.facilityZipCode}
			/>
		)
	},
	{
		header: 'Hospital Status',
		key: 'status',
		sortable: true
	},
	{
		header: 'As Of',
		key: 'statusDateTime',
		sortable: true,
		renderCell: row => formatToDateTwoYearAndTimeOrNull(row.statusDateTime, '-')
	},
	{
		header: 'Stay Started At',
		key: 'firstStatusDateTime',
		sortable: true,
		renderCell: row => formatToDateTwoYearAndTimeOrNull(row.firstStatusDateTime, '-')
	},
	{
		header: 'Setting',
		key: 'setting',
		sortable: true,
		width: 'w-[120px]'
	},
	{
		header: 'First Event',
		key: 'firstEvent',
		width: 'w-[100px]',
		renderCell: row =>
			row.firstEvent ? (
				<div className="text-center">
					<AlertCircle className="text-red-500" />
				</div>
			) : null
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function LegacyHospitalizationVisitsTable(props: TableProps) {
	return (
		<LegacyHospitalizationVisitsDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No hospitalization events found"
			{...props}
			staticFilters={[
				{ name: 'confirmed', operator: '==', value: 'false' },
				{ name: 'facilityNpi', operator: '!=', value: '1861499840' },
				{ name: 'occurredDuringChartId', operator: '!=', value: 'null' },

				...(props.staticFilters ?? [])
			]}
		/>
	);
}
